import type { OverlaySizeConfig } from '@angular/cdk/overlay';
import { DestroyRef, effect, inject, Injectable, Injector } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { debounceTime } from 'rxjs/operators';

import { SelectController } from '../../services/select-controller.service';
import { OpenStrategy } from './types';

@Injectable()
export class InlineOpenStrategy extends OpenStrategy {
  private selectController = inject(SelectController);
  private destroyRef = inject(DestroyRef);
  private injector = inject(Injector);

  public override init() {
    this.dropdownConnectedOverlay.hasBackdrop = true;
    this.dropdownConnectedOverlay.autoCloseOnUnpreventedClick = false;
    this.dropdownConnectedOverlay.origin = this.inlineTriggerOrigin;
    this.dropdownConnectedOverlay.width = this.options.overlayWidth!;
    this.dropdownConnectedOverlay.minWidth = this.options.overlayMinWidth!;
    if (this.options.overlayPosition) {
      this.dropdownConnectedOverlay.position = this.options.overlayPosition;
    }

    effect(
      () => {
        if (this.selectController.isOpen()) {
          const { width: previousWidth, minWidth: previousMinWidth } =
            this.dropdownConnectedOverlay;
          const width = this.options.overlayWidth ?? this.getDropdownWidth();
          const minWidth = this.options.overlayMinWidth;
          this.dropdownConnectedOverlay.width = width;
          this.dropdownConnectedOverlay.minWidth = minWidth!;

          if (previousWidth !== width || previousMinWidth !== minWidth) {
            this.updateOverlaySize({ width, minWidth });
          }
          this.dropdownConnectedOverlay.open = true;
        } else {
          this.dropdownConnectedOverlay.open = false;
        }
      },
      { injector: this.injector, allowSignalWrites: true },
    );

    this.dropdownConnectedOverlay.backdropClick
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.selectController.toggle(false);
      });

    this.selectController.select$
      .pipe(debounceTime(0), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (!this.options.autoClose) return;

        this.selectController.isOpen.set(false);
      });
  }

  private getDropdownWidth() {
    const triggerEl: HTMLElement = this.inlineTriggerOrigin.elementRef.nativeElement;
    return triggerEl.clientWidth;
  }

  private updateOverlaySize(sizeConfig: OverlaySizeConfig) {
    const { overlayRef } = this.dropdownConnectedOverlay;
    if (!overlayRef) return;

    overlayRef.updateSize(sizeConfig);
    this.dropdownConnectedOverlay.updatePositionWithoutRedrawing();
  }
}
